import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useRef, useEffect, lazy, Suspense } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { SceneProvider } from "@/context/SceneContext";
import { AuthProvider } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import ThreeBackground from "@/components/ThreeBackground";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import MetricsDashboard from "@/components/MetricsDashboard";
import FeatureGrid from "@/components/FeatureGrid";
import SampleVideoShowcase from "@/components/SampleVideoShowcase";
import ManifestoChapters from "@/components/ManifestoChapters";
import EditorialMarquee from "@/components/EditorialMarquee";
import DemoSection from "@/components/DemoSection";
import LeadCapture from "@/components/LeadCapture";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { useQuery } from "@tanstack/react-query";
import { fetchMetrics } from "@/lib/api";
import AuthCallback from "@/components/auth/AuthCallback";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import { CANONICAL_ORIGIN } from "@/lib/seo";

// Code-split the heavy authenticated-only pages (video rendering, growth agents, budget
// tracker, admin analytics) out of the main bundle — public/crawlable routes (Home, Blog,
// Login) no longer have to download this code just to become interactive.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const GrowthLeadPage = lazy(() => import("@/pages/GrowthLeadPage"));
const GrowthTeamPage = lazy(() => import("@/pages/GrowthTeamPage"));
const PortfolioBudget = lazy(() => import("@/pages/PortfolioBudget"));
const PublishHistory = lazy(() => import("@/pages/PublishHistory"));
const BillingResult = lazy(() => import("@/pages/BillingResult"));

const RouteFallback = () => <div className="min-h-screen bg-[#050505]" />;

const Home = () => {
  const demoRef = useRef(null);
  const metricsRef = useRef(null);

  const { data: metrics } = useQuery({
    queryKey: ["metrics"],
    queryFn: fetchMetrics,
  });

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="relative min-h-screen text-white">
      <Helmet>
        <title>Skilmark — Autonomous Marketing. Infinite Scale.</title>
        <meta
          name="description"
          content="Skilmark is your autonomous AI CMO and video production engine: instant ASO metadata, ad copy, growth loops, and viral video scripts for zero-budget app marketing."
        />
        <link rel="canonical" href={CANONICAL_ORIGIN} />
        <meta property="og:title" content="Skilmark — Autonomous Marketing. Infinite Scale." />
        <meta property="og:description" content="Instant ASO metadata, ad copy, growth loops, and viral video scripts — generated by AI for zero-budget app marketing." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${CANONICAL_ORIGIN}/logo.jpg`} />
      </Helmet>
      <Navbar onTryDemo={() => scrollTo(demoRef)} />
      <HeroSection onTryDemo={() => scrollTo(demoRef)} onSeeMetrics={() => scrollTo(metricsRef)} />
      <div ref={metricsRef}>
        <MetricsDashboard metrics={metrics || []} />
      </div>
      <FeatureGrid />
      <SampleVideoShowcase />
      <ManifestoChapters />
      <EditorialMarquee />
      <div ref={demoRef}>
        <DemoSection />
      </div>
      <LeadCapture />
      <Footer />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();

  // OAuth callback detection must happen synchronously during render (not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth-lead"
          element={
            <ProtectedRoute>
              <GrowthLeadPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth-lead/:appId"
          element={
            <ProtectedRoute>
              <GrowthLeadPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth-team"
          element={
            <ProtectedRoute>
              <GrowthTeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth-team/:appId"
          element={
            <ProtectedRoute>
              <GrowthTeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio-budget"
          element={
            <ProtectedRoute>
              <PortfolioBudget />
            </ProtectedRoute>
          }
        />
        <Route
          path="/publish-history"
          element={
            <ProtectedRoute>
              <PublishHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/success"
          element={
            <ProtectedRoute>
              <BillingResult status="success" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/cancel"
          element={
            <ProtectedRoute>
              <BillingResult status="cancel" />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

function App() {
  // Remove the static prerendered <title>/<meta>/<link rel=canonical> tags on hydration so
  // the route's own <Helmet> tags are the single source of truth (fixes duplicate canonical
  // tags on prerendered routes — see scripts/prerender.js).
  useEffect(() => {
    document.querySelectorAll('[data-prerendered="true"]').forEach((el) => el.remove());
  }, []);

  return (
    <HelmetProvider>
      <SceneProvider>
        <SmoothScroll>
          <div className="App">
            <ThreeBackground />
            <div className="grain-overlay" />
            <BrowserRouter>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </div>
        </SmoothScroll>
      </SceneProvider>
    </HelmetProvider>
  );
}

export default App;
